import React from 'react';
import {
  Box,
  Badge,
  Flex,
  Heading,
  Button,
  Text,
  Wrap,
  WrapItem,
} from '@chakra-ui/core';
import { User } from '../types';

interface ModerationPanelProps {
  user: User;
  usersVoted: User[];
  mods: User[];
  isDisabled: boolean;
  onNewVote: () => void;
  onShowResults: () => void;
  onToggleModVote: () => void;
  onStopModeration: () => void;
}

const ModerationPanel = ({
  user,
  usersVoted,
  mods,
  isDisabled,
  onNewVote,
  onShowResults,
  onToggleModVote,
  onStopModeration,
}: ModerationPanelProps) => {
  const showVotedList =
    !user.metadata.mod.allowVote ||
    mods.some((mod: User) => mod.metadata.mod.showResults);
  const moderatorNames = mods.map(mod => mod.metadata.nickname).join(', ');
  const sharingModeratorNames = mods
    .filter(mod => mod.metadata.mod.showResults)
    .map(mod => mod.metadata.nickname)
    .join(', ');

  return (
    <Box shadow="md" borderWidth="1px" borderRadius="xl" overflow="hidden">
      <Box h="1" bg="linear-gradient(to right, #9333ea, #60a5fa)" />
      <Box p={5}>
        {mods.length > 1 ? (
          <Box
            role="alert"
            bg="orange.100"
            color="orange.900"
            borderRadius="md"
            px={4}
            py={3}
            mb={4}>
            <Text fontSize="sm" fontWeight="semibold">
              {`⚠️ ${mods.length} moderators active: ${moderatorNames}. Conflicting actions may override each other — one of you can click "Stop Moderation".`}
              {sharingModeratorNames
                ? ` Results were shared by ${sharingModeratorNames}.`
                : ''}
            </Text>
          </Box>
        ) : null}
        <Heading fontSize="md" mb={4}>
          Moderation
        </Heading>
        <Wrap direction="row" spacing={3} align="center">
          <WrapItem>
            <Button size="sm" onClick={onNewVote} isDisabled={isDisabled}>
              New Vote
            </Button>
          </WrapItem>
          <WrapItem>
            <Button
              size="sm"
              colorScheme="green"
              onClick={onShowResults}
              isDisabled={isDisabled}>
              Show Results
            </Button>
          </WrapItem>
          <WrapItem>
            <Button
              size="sm"
              variant="outline"
              onClick={onToggleModVote}
              isDisabled={isDisabled}>
              {`${user.metadata.mod.allowVote ? 'Disable' : 'Enable'} Mod Vote`}
            </Button>
          </WrapItem>
          <WrapItem>
            <Button
              size="sm"
              variant="ghost"
              colorScheme="red"
              onClick={onStopModeration}
              isDisabled={isDisabled}>
              Stop Moderation
            </Button>
          </WrapItem>
        </Wrap>
        {showVotedList && usersVoted.length > 0 ? (
          <Box mt={4}>
            {usersVoted.map(votedUser => (
              <Flex
                key={votedUser.id}
                justify="space-between"
                align="center"
                py={2}
                borderBottomWidth="1px">
                <Text fontSize="sm">{votedUser.metadata.nickname}</Text>
                <Badge colorScheme="purple" borderRadius="md" px={2}>
                  {votedUser.metadata.vote}
                </Badge>
              </Flex>
            ))}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default ModerationPanel;
